// src/services/championshipService.js
// Versão completa com a nova função deleteGroup adicionada
// ✅ CORRIGIDO: getKnockoutQualifiers agora aceita targetPhase

import { configuredAxios as api } from './axiosConfig';

// ✅ FUNÇÃO AUXILIAR ATUALIZADA PARA ENVIAR OBJETOS E ARRAYS COMO JSON
const buildFormData = (data) => {
  const formData = new FormData();
  
  // Lista de campos que são Objetos ou Arrays e precisam ser enviados como String JSON
  // para evitar que o FormData os transforme em "item1,item2" (o que quebra o backend)
  const jsonFields = ['suspensionRules', 'customFormatConfig', 'rankingCriteria'];

  Object.entries(data).forEach(([key, value]) => {
    
    // Verifica se a chave está na lista de campos JSON e se o valor é um objeto/array válido
    if (jsonFields.includes(key) && typeof value === 'object' && value !== null) {
      
      // Regra específica: Apenas envia `customFormatConfig` se o formato for o correto
      if (key === 'customFormatConfig' && data.format !== 'Formato Personalizado') {
          return; // Pula este campo se não for necessário
      }
      
      // Converte o objeto/array em uma string JSON para envio (Ex: '["points", "wins"]')
      formData.append(key, JSON.stringify(value));
    
    } else if (value instanceof File) { // <--- NOVO: SE É UM OBJETO File
      formData.append(key, value);
    } else if (key === 'headerImage' && value === null) { // <--- NOVO: SE É EXPLICITAMENTE NULL para remover
      formData.append(key, 'null'); // Envia a string "null" para o backend saber que é para remover
    } else if (key === 'logo' && value === null) { // <--- Opcional: para logo também
      formData.append(key, 'null');
    }
    else if (value !== undefined && value !== null) {
      // Para outros campos (name, location, etc.), anexa normalmente
      formData.append(key, value);
    }
  });
  
  return formData;
};

const ChampionshipService = {
  // --- Campeonatos ---
  create: (data) => {
    const formData = buildFormData(data);
    return api.post('/championships', formData);
  },

  getById: (id) => api.get(`/championships/${id}`).then(res => res.data),

  getBySlug: (slug) => api.get(`/championships/slug/${slug}`).then(res => res.data),

  getAll: (params = {}) => api.get('/championships', { params }),

  update: (id, data) => {
    const formData = buildFormData(data);
    return api.put(`/championships/${id}`, formData);
  },

  delete: (id) => api.delete(`/championships/${id}`),

  // Remove a team from a specific championship
  removeTeamFromChampionship: (championshipId, teamId) => {
    return api.delete(`/championships/${championshipId}/teams/${teamId}`);
  },

  addTeams: (id, teamIds, groupName) =>
    api.post(`/championships/${id}/teams`, { teamIds, groupName }),

  getPaginated: (page = 1, limit = 10, filters = {}) =>
    api.get('/championships/paginated', {
      params: { page, limit, ...filters }
    }),

  generateGroups: (id) =>
    api.post(`/championships/${id}/generate-groups`),

  generateGroupMatches: (id) =>
    api.post(`/championships/${id}/generate-group-matches`),

  getMatchesByChampionship: (id) =>
    api.get(`/championships/${id}/matches`).then(res => res.data),

  getStandings: (id) =>
    api.get(`/championships/${id}/standings`).then(res => res.data),

  // --- Gestores ---
  addManager: (championshipId, email) =>
    api.post(`/championships/${championshipId}/managers`, { email }),

  removeManager: (championshipId, managerId) =>
    api.delete(`/championships/${championshipId}/managers/${managerId}`),

  // --- Gerenciamento de Grupos ---
  getUnassignedTeams: (id) => 
    api.get(`/championships/${id}/unassigned-teams`).then(res => res.data),

  createGroup: (id, groupData) =>
    api.post(`/championships/${id}/groups`, groupData).then(res => res.data),

  moveTeamToGroup: (id, data) => 
    api.patch(`/championships/${id}/groups/move-team`, data).then(res => res.data),

  // ✅ NOVA FUNÇÃO: Excluir um grupo
  deleteGroup: (championshipId, groupName) => {
    // encodeURIComponent é usado para lidar com caracteres especiais no nome do grupo (espaços, acentos, etc.)
    return api.delete(`/championships/${championshipId}/groups/${encodeURIComponent(groupName)}`).then(res => res.data);
  },

  // --- Times ---
  createTeam: (data) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
    return api.post('/teams', formData);
  },

  getAllTeams: (params = {}) => api.get('/teams', { params }),

  getTeamById: (id) => api.get(`/teams/${id}`).then(res => res.data),

  updateTeam: (id, data) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
    return api.put(`/teams/${id}`, formData);
  },

  deleteTeam: (id) => api.delete(`/teams/${id}`),

  // --- Partidas ---
  getAllFormattedMatches: () =>
    api.get('/matches/formatted').then(res => res.data),

  updateMatch: (matchId, data) =>
    api.put(`/matches/${matchId}`, data),

  registerMatch: (id, matchData) =>
    api.post(`/championships/${id}/matches`, matchData).then(res => res.data),

  // --- Formato do Campeonato ---
  generateKnockout: (id) =>
    api.post(`/championships/${id}/generate-knockout`).then(res => res.data),

  // --- Mata-mata Manual ---
  createManualKnockout: (championshipId, pairings, phase) => {
    return api.post(`/championships/${championshipId}/manual-knockout`, { pairings, phase });
  },

  // ✅ CORRIGIDO: Agora aceita targetPhase para buscar perdedores (3º lugar)
  getKnockoutQualifiers: (championshipId, previousPhase = 'initial', targetPhase = '') => {
    const params = { previousPhase };
    if (targetPhase) {
      params.targetPhase = targetPhase;
    }
    return api.get(`/championships/${championshipId}/knockout-qualifiers`, { params });
  },

  setupManualPairings: (championshipId, pairings, phase) => {
    return api.post(`/championships/${championshipId}/manual-knockout`, { pairings, phase });
  }
};

export default ChampionshipService;